import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose, withHandlers, withProps, branch, renderComponent } from 'recompose';
import { reduxForm, Form, Field } from 'redux-form';
import { requestResendConfirmationEmail } from '../../actions/user';
import { withModal } from '../../hoc/withModal';
import { credentialsSelector } from '../../selectors';
import { containerLoadStateSelector } from '../../selectors/container-load-state';
import FormField from '../form-field/FormField';
import SendingAnimation from '../sending-animation/SendingAnimation';

import './UserEmailConfirmationModal.scss';

const UserEmailConfirmationSuccess = ({
  containerLoadState: {
    success,
  },
}) => (
  <div className="user-email-confirmation-success">
    { success && (
      <FormattedHTMLMessage
        id="UserEmailConfirmationModal.pleaseClickEmailLink"
        defaultMessage="Pour poursuivre, merci de<br/> cliquer sur le lien dans l'email."
        description="Message de success du renvoi du mail de confirmation"
      >
        { txt => (<p className="success-message invite-click-email-link" dangerouslySetInnerHTML={{__html: txt}}/>) }
      </FormattedHTMLMessage>
    )}
    <SendingAnimation
      success={success}
    />
    { success && (
      <FormattedMessage
        id="UserEmailConfirmationModal.success.title"
        defaultMessage="Succès"
        description="Titre de la modale de succès de renvoi du mail de confirmation"
      >
        { txt => <h3>{txt}</h3> }
      </FormattedMessage>
    )}
    { success && (
      <FormattedMessage
        id="UserEmailConfirmationModal.emailSended"
        defaultMessage="Un email de confirmation a été envoyé à {email}."
        description="Message de success du renvoi du mail de confirmation"
        values={{
          email: success,
        }}
      >
        { txt => (<p className="success-message email-sended">{txt}</p>) }
      </FormattedMessage>
    )}
  </div>
);

UserEmailConfirmationSuccess.propTypes = {
  containerLoadState: PropTypes.shape({
    success: PropTypes.string,
    sending: PropTypes.bool,
  }).isRequired,
};

const UserEmailConfirmationModal = ({
  email,
  handleSubmit,
}) => (
  <div className="user-email-confirmation">
    <FormattedMessage
      id="UserEmailConfirmationModal.title"
      defaultMessage="Merci de confirmer votre adresse email"
      description="Modal demandant la confirmation du mail de l'utilisateur"
    >
      { txt => (<h1 className="title">{txt}</h1>) }
    </FormattedMessage>
    <FormattedMessage
      id="UserEmailConfirmationModal.description.p1"
      defaultMessage="Nous vous avons envoyé un email à"
      description="Information concernant l'envoi précédent d'un email à une adresse mail définie"
    >
      { txt => (<p className="description">{txt}</p>) }
    </FormattedMessage>
    <p className="email-highlight">{email}.</p>
    <FormattedMessage
      id="UserEmailConfirmationModal.description.p2"
      defaultMessage="Merci de cliquer sur le lien dans l'email pour confirmer votre adresse email."
      description="Information concernant l'envoi précédent d'un email à une adresse mail définie"
    >
      { txt => (<p className="description">{txt}</p>) }
    </FormattedMessage>
    <Form onSubmit={handleSubmit}>
      <Field
        name="email"
        component={FormField}
        type="text"
      />
      <FormattedMessage
        id="UserEmailConfirmationModal.confirmButton"
        defaultMessage="Renvoyer le mail de confirmation"
        description="Bouton de confirmation de renvoi du mail"
      >
        {txt => (
          <Button
            bsStyle="primary"
            type="submit"
          >
            {txt}
          </Button>
        )}
      </FormattedMessage>
    </Form>
  </div>
);

UserEmailConfirmationModal.propTypes = {
  email: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default compose(
  connect(state => ({
    credentials: credentialsSelector(state),
    containerLoadState: containerLoadStateSelector('requestUserEmailConfirmation')(state),
  })),
  withProps(({ credentials }) => ({
    email: credentials?.[0]?.attributes?.email,
  })),
  withModal({
    id: 'user-email-confirmation-modal',
  }),
  branch(
    ({ containerLoadState }) => containerLoadState?.success || containerLoadState?.sending,
    renderComponent(UserEmailConfirmationSuccess)
  ),
  withProps(({ email }) => ({
    initialValues: {
      email,
    },
  })),
  withHandlers({
    onSubmit: ({ dispatch }) => ({ email }) => {
      dispatch(requestResendConfirmationEmail(email));
    },
  }),
  reduxForm({
    form: 'user-email-confirmation-modal',
  })
)(UserEmailConfirmationModal);
